import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n/useTranslation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BookingModal = ({ slot, onClose, onSuccess }) => {
  const { user } = useAuth();
  const { t, language } = useTranslation();
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [fieldsError, setFieldsError] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    setFieldsLoading(true);
    setFieldsError(false);
    try {
      const response = await axios.get(`${API_URL}/bookings/fields`);
      setFields(response.data);
      // Initialize form data
      const initialData = {};
      response.data.forEach(field => {
        initialData[field.name] = '';
      });
      setFormData(initialData);
    } catch (error) {
      console.error('Error fetching fields:', error);
      setFieldsError(true);
    } finally {
      setFieldsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Never submit before the field definitions have resolved - otherwise the
    // validation below passes trivially and an empty booking is stored.
    if (fieldsLoading) {
      setError(t('booking.stillLoading'));
      return;
    }

    if (fieldsError) {
      setError(t('booking.fieldsFailed'));
      return;
    }

    setLoading(true);

    // Validate required fields
    const missingFields = fields
      .filter(field => field.required && !formData[field.name])
      .map(field => field.label);
    
    if (missingFields.length > 0) {
      setError(`${t('validation.required')}: ${missingFields.join(', ')}`);
      setLoading(false);
      return;
    }

    try {
      const bookingData = {
        slotId: slot._id,
        bookingFields: formData
      };

      // Guest bookings don't require email or name - can be added via custom fields if needed

      await axios.post(`${API_URL}/bookings`, bookingData);
      onSuccess();
    } catch (err) {
      const data = err.response?.data;
      if (data?.missingFields?.length) {
        setError(`${t('validation.required')}: ${data.missingFields.join(', ')}`);
      } else {
        setError(data?.message || 'Booking failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // Always use Gregorian calendar, but with Arabic month names if Arabic is selected
    const monthNames = language === 'ar'
      ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
      : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = language === 'ar'
      ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
      : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    const day = dayNames[date.getDay()];
    const month = monthNames[date.getMonth()];
    const dayNum = date.getDate();
    const year = date.getFullYear();
    
    return language === 'ar' 
      ? `${day}، ${dayNum} ${month} ${year}`
      : `${day}, ${month} ${dayNum}, ${year}`;
  };

  const renderField = (field) => {
    const inputClass = `mt-1 block w-full px-4 py-3 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition ${isRTL ? 'text-right' : 'text-left'}`;
    
    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            name={field.name}
            value={formData[field.name] || ''}
            onChange={handleChange}
            placeholder={field.placeholder}
            required={field.required}
            className={inputClass}
            rows={4}
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        );
      case 'select':
        return (
          <div className="relative">
            <select
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              required={field.required}
              className={`${inputClass} bg-white cursor-pointer hover:border-blue-400 focus:shadow-lg`}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <option value="" disabled className="text-gray-400">
                {t('booking.select')} {field.label}
              </option>
              {field.options.map((option, idx) => (
                <option key={idx} value={option} className="py-2">
                  {option}
                </option>
              ))}
            </select>
          </div>
        );
      default:
        return (
          <input
            type={field.type}
            name={field.name}
            value={formData[field.name] || ''}
            onChange={handleChange}
            placeholder={field.placeholder}
            required={field.required}
            className={inputClass}
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        );
    }
  };

  const isRTL = language === 'ar';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] sm:max-h-[90vh] overflow-hidden my-4 sm:my-8 flex flex-col" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Fixed Header */}
        <div className="p-4 sm:p-6 lg:p-8 border-b border-gray-200 flex-shrink-0">
          {/* Header */}
          <div className={`flex justify-between items-start mb-4 sm:mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex-1">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">{t('booking.title')}</h2>
              <p className="text-sm sm:text-base text-gray-600 mt-1">{t('booking.subtitle')}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition p-2 hover:bg-gray-100 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Appointment Info */}
          <div className="mb-4 sm:mb-6 p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-100" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className={`flex items-center gap-2 sm:gap-3 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-2 sm:p-3 bg-blue-600 rounded-lg flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600">{t('booking.date')}</p>
                <p className="text-base sm:text-lg font-semibold text-gray-900 break-words">{formatDate(slot.date)}</p>
              </div>
            </div>
            <div className={`flex items-center gap-2 sm:gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-2 sm:p-3 bg-indigo-600 rounded-lg flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600">{t('booking.time')}</p>
                <p className="text-base sm:text-lg font-semibold text-gray-900">
                  {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 sm:mb-6 bg-red-100 border-2 border-red-400 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 custom-scrollbar relative">
          {/* Scroll indicator at bottom */}
          <div className="modal-scroll-indicator"></div>
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Loading state - the form must never look submittable while empty */}
            {fieldsLoading && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-600">{t('booking.loadingFields')}</p>
              </div>
            )}

            {/* Fetch failed - offer a retry instead of a silently blank form */}
            {!fieldsLoading && fieldsError && (
              <div className="py-8 text-center">
                <p className="text-red-600 mb-4">{t('booking.fieldsFailed')}</p>
                <button
                  type="button"
                  onClick={fetchFields}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  {t('booking.retry')}
                </button>
              </div>
            )}

            {/* Booking Fields */}
            {!fieldsLoading && !fieldsError && fields.length > 0 && (
              <div dir={isRTL ? 'rtl' : 'ltr'}>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('booking.additionalInfo')}</h3>
                <div className="space-y-4">
                  {fields.map((field) => (
                    <div key={field._id}>
                      <label htmlFor={field.name} className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {field.label}
                        {field.required && <span className={`text-red-500 ${isRTL ? 'mr-1' : 'ml-1'}`}>*</span>}
                      </label>
                      {field.description && (
                        <div 
                          className={`text-gray-600 mb-2 rich-text-content ${isRTL ? 'text-right' : 'text-left'}`}
                          dangerouslySetInnerHTML={{ __html: field.description }}
                        />
                      )}
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              </div>
            )}

          </form>
        </div>

        {/* Fixed Footer */}
        <div className={`p-4 sm:p-6 lg:p-8 border-t border-gray-200 bg-gray-50 flex-shrink-0 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
          <div className={`flex flex-col sm:flex-row justify-end gap-2 sm:gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium text-sm sm:text-base"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || fieldsLoading || fieldsError}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold shadow-md hover:shadow-lg text-sm sm:text-base"
            >
              {loading
                ? t('booking.booking')
                : fieldsLoading
                  ? t('booking.loadingFields')
                  : t('booking.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
